import React, { useState, useEffect, useCallback } from 'react';
import Logo from '../components/Logo';
import TecladoNumerico from '../components/TecladoNumerico';
import {
  validarUsuario,
  verificarPedidoExistente,
  getMenuDiaSiguiente,
  crearPedido,
} from '../services/api';
import '../styles/Totem.css';

const TIMEOUT_SECONDS = 30;

function TotemLayout() {
  const [step, setStep] = useState('dni'); // dni, confirmar, menu, exito
  const [dni, setDni] = useState('');
  const [usuario, setUsuario] = useState(null);
  const [menu, setMenu] = useState([]);
  const [selecciones, setSelecciones] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(TIMEOUT_SECONDS);
  const [fechaMañana, setFechaMañana] = useState('');

  // Reset timeout counter on user interaction
  const resetTimeout = useCallback(() => {
    setCountdown(TIMEOUT_SECONDS);
  }, []);

  // Reset a inicio
  const resetToInicio = useCallback(() => {
    setStep('dni');
    setDni('');
    setUsuario(null);
    setMenu([]);
    setSelecciones({});
    setError(null);
    setCountdown(TIMEOUT_SECONDS);
  }, []);

  // Timeout management
  useEffect(() => {
    if (step === 'dni') {
      setCountdown(TIMEOUT_SECONDS);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          resetToInicio();
          return TIMEOUT_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, resetToInicio]);

  // Handlers del teclado numérico
  const handleNumberClick = (num) => {
    resetTimeout();
    if (dni.length < 10) {
      setDni(dni + num);
    }
  };

  const handleBackspace = () => {
    resetTimeout();
    setDni(dni.slice(0, -1));
  };

  const handleConfirmDni = async () => {
    resetTimeout();
    if (!dni || dni.length < 6) {
      setError('Ingresa un DNI válido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validar usuario
      const userRes = await validarUsuario(dni);
      const user = userRes.data.usuario;
      setUsuario(user);

      // Verificar si ya tiene pedido
      const pedidoRes = await verificarPedidoExistente(dni);
      if (pedidoRes.data.tienePedido) {
        setError('Ya tienes un pedido para mañana. Solo se permite uno por día.');
        setTimeout(() => {
          resetToInicio();
        }, 5000);
        return;
      }

      // Cargar menú usando la sede del usuario
      const sedeId = user.sede?.id || 1; // Default a sede 1 si no tiene
      const menuRes = await getMenuDiaSiguiente(sedeId);
      setMenu(menuRes.data.menu);

      // Calcular fecha de mañana
      const mañana = new Date();
      mañana.setDate(mañana.getDate() + 1);
      setFechaMañana(
        mañana.toLocaleDateString('es-AR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })
      );

      setStep('confirmar');
    } catch (error) {
      console.error('Error validando usuario:', error);
      setError(error.response?.data?.error || 'DNI no encontrado. Intenta nuevamente.');
      setTimeout(() => {
        setError(null);
        setDni('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarContinuar = () => {
    resetTimeout();
    if (menu.length === 0) {
      setError('No hay menú disponible para mañana');
      setTimeout(() => resetToInicio(), 3000);
      return;
    }
    setStep('menu');
  };

  const toggleSeleccion = (categoriaId, platoId) => {
    resetTimeout();
    setSelecciones((prev) => ({
      ...prev,
      [categoriaId]: platoId,
    }));
  };

  const handleConfirmarPedido = async () => {
    resetTimeout();

    // Validar selecciones
    const tieneCategoria = menu.find((cat) => cat.categoria === 'Principal');
    if (tieneCategoria && !selecciones[tieneCategoria.categoria_id]) {
      setError('Debes seleccionar al menos un plato principal');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (Object.keys(selecciones).length === 0) {
      setError('Debes seleccionar al menos un plato');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const platos = Object.values(selecciones).map((plato_id) => ({ plato_id }));

      await crearPedido({
        dni: usuario.dni,
        platos: platos,
        tipo_interfaz: 'totem',
      });

      setStep('exito');
      setTimeout(() => {
        resetToInicio();
      }, 5000);
    } catch (error) {
      console.error('Error creando pedido:', error);
      setError(error.response?.data?.error || 'Error al crear el pedido');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    resetTimeout();
    if (step === 'menu') {
      setStep('confirmar');
      setSelecciones({});
    } else if (step === 'confirmar') {
      resetToInicio();
    }
  };

  return (
    <div className="totem-container">
      {step !== 'dni' && (
        <div className="totem-header">
          <Logo size="large" showText={true} style={{ filter: 'brightness(0) invert(1)' }} />
          {step !== 'exito' && (
            <p style={{ fontSize: '20px', marginTop: '10px', color: 'rgba(255, 255, 255, 0.9)' }}>
              Tiempo restante: {countdown}s
            </p>
          )}
        </div>
      )}

      <div className="totem-content">
        {/* PASO 1: Ingresar DNI */}
        {step === 'dni' && (
          <div className="dni-screen">
            {error && <div className="alert-totem alert-totem-error">{error}</div>}

            <div className={`dni-display ${!dni ? 'empty' : ''}`}>
              {dni || 'COLOQUE SU DNI'}
            </div>

            {loading ? (
              <div className="loading-totem">
                <div className="spinner"></div>
                <p>Validando...</p>
              </div>
            ) : (
              <TecladoNumerico
                onNumberClick={handleNumberClick}
                onBackspace={handleBackspace}
                onConfirm={handleConfirmDni}
                dniValue={dni}
              />
            )}
          </div>
        )}

        {/* PASO 2: Confirmación de usuario */}
        {step === 'confirmar' && usuario && (
          <div className="confirmation-screen">
            <h2>¡HOLA!</h2>
            <div className="user-name">{usuario.nombreCompleto}</div>
            <div className="date-info">Vas a pedir para {fechaMañana}</div>

            {error && <div className="alert-totem alert-totem-error">{error}</div>}

            <div className="confirmation-buttons">
              <button className="btn-totem btn-totem-danger" onClick={handleVolver}>
                ✗ CANCELAR
              </button>
              <button className="btn-totem btn-totem-success" onClick={handleConfirmarContinuar}>
                ✓ CONTINUAR
              </button>
            </div>
          </div>
        )}

        {/* PASO 2: Selección de menú */}
        {step === 'menu' && (
          <div>
            <h2 style={{ fontSize: '42px', textAlign: 'center', marginBottom: '30px', color: '#2c3e50' }}>
              SELECCIONÁ TU MENÚ
            </h2>

            {error && <div className="alert-totem alert-totem-error">{error}</div>}

            {menu.length === 0 ? (
              <div className="loading-totem">
                <p>No hay menú disponible</p>
              </div>
            ) : (
              <>
                {menu.map((categoria) => (
                  <div key={categoria.categoria_id} className="menu-category-totem">
                    <h3>
                      {categoria.categoria}
                      {selecciones[categoria.categoria_id] && (
                        <span style={{ color: '#27ae60', marginLeft: '20px' }}>✓</span>
                      )}
                    </h3>
                    <div className="menu-grid-totem">
                      {categoria.platos.map((plato) => (
                        <div
                          key={plato.id}
                          className={`menu-card-totem ${
                            selecciones[categoria.categoria_id] === plato.id ? 'selected' : ''
                          }`}
                          onClick={() => toggleSeleccion(categoria.categoria_id, plato.id)}
                        >
                          {plato.imagen && <img src={plato.imagen} alt={plato.nombre} />}
                          <div className="menu-card-content">
                            <h4>{plato.nombre}</h4>
                            <p>{plato.descripcion}</p>
                            {selecciones[categoria.categoria_id] === plato.id && (
                              <span className="selected-badge-totem">SELECCIONADO</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="nav-buttons">
                  <button className="btn-totem btn-totem-secondary" onClick={handleVolver}>
                    ← VOLVER
                  </button>
                  <button
                    className="btn-totem btn-totem-success"
                    onClick={handleConfirmarPedido}
                    disabled={loading || Object.keys(selecciones).length === 0}
                  >
                    {loading ? 'CONFIRMANDO...' : '✓ CONFIRMAR PEDIDO'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* PASO 3: Éxito */}
        {step === 'exito' && (
          <div className="success-screen">
            <div className="success-icon">✓</div>
            <h2>¡PEDIDO CONFIRMADO!</h2>
            <p>Tu pedido para {fechaMañana} fue registrado exitosamente</p>
            <div className="countdown">
              Cerrando sesión en {countdown} segundos...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TotemLayout;
